import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as adminService from '@/services/adminService';

const TYPE_LABELS = {
  mission: { label: 'Missão', color: 'text-forest-light', border: 'border-forest-light/30', bg: 'bg-forest-light/10' },
  poi: { label: 'POI', color: 'text-ocean-light', border: 'border-ocean-light/30', bg: 'bg-ocean-light/10' },
  zone: { label: 'Zona', color: 'text-accent', border: 'border-accent/30', bg: 'bg-accent/10' },
  badge: { label: 'Medalha', color: 'text-gem', border: 'border-gem/30', bg: 'bg-gem/10' },
};

const STATUS_LABELS = {
  pending: { label: 'Pendente', dot: 'bg-accent', text: 'text-accent' },
  approved: { label: 'Aprovada', dot: 'bg-forest-light', text: 'text-forest-light' },
  rejected: { label: 'Rejeitada', dot: 'bg-energy', text: 'text-energy' },
  backlog: { label: 'Backlog', dot: 'bg-fg/40', text: 'text-fg/50' },
};

const TABS = [
  { key: 'pending', label: 'Pendentes' },
  { key: 'backlog', label: 'Backlog' },
  { key: 'approved', label: 'Aprovadas' },
  { key: 'rejected', label: 'Rejeitadas' },
];

// Passos do pipeline para animação
const PIPELINE_STEPS = [
  { id: 'researcher', label: 'Pesquisador', icon: '🔍', desc: 'A descobrir POIs reais na cidade...' },
  { id: 'strategist', label: 'Estrategista', icon: '✍️', desc: 'A escrever descrições e recompensas...' },
  { id: 'designer', label: 'Designer', icon: '🎨', desc: 'A gerar imagens do POI e da medalha...' },
  { id: 'reviewer', label: 'Revisor', icon: '✅', desc: 'A validar e remover duplicados...' },
];

// ─── Modal de nota (aprovar/rejeitar/backlog) ─────────────────────────────────
function NoteModal({ open, onClose, onConfirm, action, loading }) {
  const [note, setNote] = useState('');
  useEffect(() => { if (!open) setNote(''); }, [open]);
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const cfg = {
    approve: { title: 'Aprovar sugestão', btn: 'Aprovar e criar no jogo', cls: 'border-forest-light/40 bg-forest-light/10 text-forest-light hover:bg-forest-light/20' },
    reject: { title: 'Rejeitar sugestão', btn: 'Confirmar rejeição', cls: 'border-energy/40 bg-energy/10 text-energy hover:bg-energy/20' },
    backlog: { title: 'Mover para backlog', btn: 'Mover para backlog', cls: 'border-fg/25 bg-fg/5 text-fg/60 hover:bg-fg/10' },
  }[action] || {};

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
          <motion.div className="w-full max-w-md rounded-xl border border-accent/20 bg-[#0f1117] p-6 shadow-2xl"
            initial={{ scale: 0.93, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.93, opacity: 0 }}
            transition={{ duration: 0.18 }}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-xl font-black text-accent">{cfg.title}</h3>
              <button onClick={onClose} className="text-fg/40 hover:text-fg/80 text-lg">✕</button>
            </div>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Nota opcional..."
              className="w-full rounded-lg border border-fg/15 bg-fg/5 p-3 text-sm text-fg placeholder-white/25 focus:border-accent/50 focus:outline-none resize-none" />
            <div className="mt-4 flex gap-2">
              <button onClick={onClose} className="flex-1 rounded-lg border border-fg/15 py-2 text-sm text-fg/60 hover:bg-fg/5">Cancelar</button>
              <button onClick={() => onConfirm(note)} disabled={loading}
                className={`flex-1 rounded-lg border py-2 text-sm font-bold disabled:opacity-50 ${cfg.cls}`}>
                {loading ? 'A processar...' : cfg.btn}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Modal do Pipeline POI Research ──────────────────────────────────────────
function POIResearchModal({ open, onClose, onSuccess }) {
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState({ cityId: '', zoneId: '', limit: 5, generateImages: false });
  const [step, setStep] = useState('form'); // 'form' | 'running' | 'done' | 'error'
  const [activeStep, setActiveStep] = useState(0);
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState('');
  const stepTimer = useRef(null);

  useEffect(() => {
    if (!open) { setStep('form'); setActiveStep(0); setResult(null); setErrMsg(''); return; }
    adminService.cities.list({ limit: 50 }).then((r) => setCities(r.data.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (form.cityId) {
      adminService.zones.list({ limit: 50 }).then((r) => {
        const all = r.data.data || [];
        setZones(all.filter((z) => z.cityId === form.cityId));
      }).catch(() => {});
    } else {
      setZones([]);
    }
    setForm((f) => ({ ...f, zoneId: '' }));
  }, [form.cityId]);

  // Anima os passos do pipeline durante a execução
  useEffect(() => {
    if (step !== 'running') { clearInterval(stepTimer.current); return; }
    setActiveStep(0);
    let i = 0;
    // Cada passo dura ~15s se houver imagens, ~8s sem
    const interval = form.generateImages ? 18000 : 8000;
    stepTimer.current = setInterval(() => {
      i += 1;
      if (i < PIPELINE_STEPS.length) setActiveStep(i);
      else clearInterval(stepTimer.current);
    }, interval);
    return () => clearInterval(stepTimer.current);
  }, [step, form.generateImages]);

  const run = async () => {
    if (!form.cityId) return;
    setStep('running');
    setErrMsg('');
    try {
      const res = await adminService.suggestions.researchPOIs({
        cityId: form.cityId,
        zoneId: form.zoneId || null,
        limit: Number(form.limit),
        generateImages: form.generateImages,
      });
      setResult(res.data.data);
      setStep('done');
      onSuccess();
    } catch (e) {
      setErrMsg(e.response?.data?.message || 'Erro ao executar o pipeline.');
      setStep('error');
    }
  };

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && step !== 'running') onClose(); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, step, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={(e) => { if (e.target === e.currentTarget && step !== 'running') onClose(); }}>
          <motion.div className="w-full max-w-lg rounded-xl border border-accent/20 bg-[#0f1117] p-6 shadow-2xl"
            initial={{ scale: 0.93, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.93, opacity: 0 }}
            transition={{ duration: 0.18 }}>

            {/* Header */}
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent/60">Pipeline multi-agente</p>
                <h3 className="font-serif text-2xl font-black text-accent">Pesquisar novos POIs</h3>
              </div>
              {step !== 'running' && (
                <button onClick={onClose} className="text-fg/40 hover:text-fg/80 text-lg">✕</button>
              )}
            </div>

            {/* Passos do pipeline — sempre visíveis */}
            <div className="mb-5 grid grid-cols-4 gap-2">
              {PIPELINE_STEPS.map((s, i) => {
                const isDone = step === 'done' || (step === 'running' && i < activeStep);
                const isActive = step === 'running' && i === activeStep;
                return (
                  <div key={s.id} className={`rounded-lg border p-2 text-center transition-all ${
                    isDone ? 'border-forest-light/40 bg-forest-light/10' :
                    isActive ? 'border-accent/40 bg-accent/10' :
                    'border-fg/10 bg-black/10'
                  }`}>
                    <div className={`text-xl ${isActive ? 'animate-bounce' : ''}`}>{s.icon}</div>
                    <div className={`mt-1 text-xs font-bold ${isActive ? 'text-accent' : isDone ? 'text-forest-light' : 'text-fg/35'}`}>
                      {s.label}
                    </div>
                    {isDone && <div className="text-[10px] text-forest-light/70 mt-0.5">✓</div>}
                  </div>
                );
              })}
            </div>

            {/* Formulário */}
            {step === 'form' && (
              <>
                <div className="grid gap-3">
                  <label className="grid gap-1">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-fg/40">Cidade *</span>
                    <select
                      value={form.cityId}
                      onChange={(e) => setForm((f) => ({ ...f, cityId: e.target.value }))}
                      className="w-full rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent"
                    >
                      <option value="">Seleciona uma cidade...</option>
                      {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-fg/40">Zona (opcional)</span>
                    <select
                      value={form.zoneId}
                      onChange={(e) => setForm((f) => ({ ...f, zoneId: e.target.value }))}
                      disabled={!form.cityId || zones.length === 0}
                      className="w-full rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent disabled:opacity-40"
                    >
                      <option value="">Toda a cidade</option>
                      {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                    </select>
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-fg/40">Quantos POIs (1–10)</span>
                    <input
                      type="number" min="1" max="10"
                      value={form.limit}
                      onChange={(e) => setForm((f) => ({ ...f, limit: e.target.value }))}
                      className="w-full rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent"
                    />
                  </label>

                  <label className="flex items-center gap-3 rounded-lg border border-fg/10 bg-black/15 p-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.generateImages}
                      onChange={(e) => setForm((f) => ({ ...f, generateImages: e.target.checked }))}
                      className="h-4 w-4 accent-gold"
                    />
                    <div>
                      <div className="text-sm font-bold text-fg/80">Gerar imagens com DALL-E</div>
                      <div className="text-xs text-fg/40">Imagem do POI + medalha. Mais lento (~2 min) e consome créditos OpenAI.</div>
                    </div>
                  </label>
                </div>

                <div className="mt-5 flex gap-2">
                  <button onClick={onClose} className="flex-1 rounded-lg border border-fg/15 py-2.5 text-sm text-fg/60 hover:bg-fg/5">
                    Cancelar
                  </button>
                  <button
                    onClick={run}
                    disabled={!form.cityId}
                    className="flex-1 rounded-lg border border-accent/40 bg-accent/10 py-2.5 text-sm font-bold text-accent hover:bg-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Lançar pipeline
                  </button>
                </div>
              </>
            )}

            {/* A correr */}
            {step === 'running' && (
              <div className="text-center py-4">
                <div className="text-sm font-bold text-accent mb-1">
                  {PIPELINE_STEPS[activeStep]?.label} a trabalhar...
                </div>
                <p className="text-xs text-fg/50">{PIPELINE_STEPS[activeStep]?.desc}</p>
                <p className="mt-4 text-xs text-fg/30">
                  {form.generateImages ? 'Com imagens DALL-E — pode demorar 2-3 minutos.' : 'Sem imagens — pronto em ~30 segundos.'}
                </p>
              </div>
            )}

            {/* Concluído */}
            {step === 'done' && result && (
              <div className="text-center py-2">
                <div className="text-3xl mb-2">🎉</div>
                <p className="font-bold text-forest-light text-lg">{result.savedSuggestions} novo(s) POI(s) descobertos!</p>
                <p className="text-xs text-fg/50 mt-1">
                  {result.cityName}{result.zoneName ? ` / ${result.zoneName}` : ''} · {Math.round(result.durationMs / 1000)}s
                </p>
                <div className="mt-3 rounded-lg border border-fg/10 bg-black/15 p-3 text-left space-y-1">
                  {result.pois?.slice(0, 5).map((poi, i) => (
                    <div key={i} className="text-sm text-fg/70">
                      <span className="font-bold text-ocean-light">·</span> {poi.name}
                      {poi.imageUrl && <span className="ml-1 text-xs text-forest-light/60">+ imagem</span>}
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-fg/40">Vê os cartões em "Pendentes" para aprovar ou rejeitar.</p>
                <button onClick={onClose} className="mt-4 rounded-lg border border-accent/40 bg-accent/10 px-6 py-2 text-sm font-bold text-accent hover:bg-accent/20">
                  Ver sugestões
                </button>
              </div>
            )}

            {/* Erro */}
            {step === 'error' && (
              <div className="text-center py-2">
                <div className="text-3xl mb-2">❌</div>
                <p className="font-bold text-energy">{errMsg}</p>
                <div className="mt-4 flex gap-2 justify-center">
                  <button onClick={() => setStep('form')} className="rounded-lg border border-fg/15 px-4 py-2 text-sm text-fg/60 hover:bg-fg/5">Tentar novamente</button>
                  <button onClick={onClose} className="rounded-lg border border-energy/30 px-4 py-2 text-sm font-bold text-energy hover:bg-energy/10">Fechar</button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Card de sugestão ─────────────────────────────────────────────────────────
function SuggestionCard({ item, onAction, onDelete, acting }) {
  const type = TYPE_LABELS[item.type] || TYPE_LABELS.poi;
  const status = STATUS_LABELS[item.status] || STATUS_LABELS.pending;
  const canAct = item.status === 'pending' || item.status === 'backlog';
  const isPending = item.status === 'pending';
  const rawData = item.data;
  const d = typeof rawData === 'string' ? (() => { try { return JSON.parse(rawData); } catch { return {}; } })() : (rawData || {});
  const fromPipeline = item.generatedBy === 'POIResearchAgent';

  const SHOW_KEYS = {
    poi: ['name', 'category', 'lat', 'lng', 'coinReward', 'xpReward', 'collectionRadius'],
    mission: ['title', 'type', 'coinReward', 'xpReward', 'requirement'],
    zone: ['name', 'color', 'description'],
    badge: ['name', 'rarity', 'coinReward', 'xpReward'],
  };
  const allowedKeys = SHOW_KEYS[item.type] || [];
  const dataEntries = allowedKeys.map((k) => [k, d[k]]).filter(([, v]) => v !== null && v !== undefined);

  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
      className="rounded-lg border border-fg/10 bg-fg/[0.03] p-4">

      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`rounded-full border px-2.5 py-0.5 text-xs font-bold ${type.color} ${type.border} ${type.bg}`}>
            {type.label}
          </span>
          {fromPipeline && (
            <span className="rounded-full border border-gem/25 bg-gem/8 px-2 py-0.5 text-[10px] font-bold text-gem/80">
              🤖 Pipeline
            </span>
          )}
          <h3 className="font-bold text-fg leading-snug">{item.title}</h3>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`h-2 w-2 rounded-full ${status.dot}`} />
          <span className={`text-xs font-bold ${status.text}`}>{status.label}</span>
        </div>
      </div>

      {item.reason && (
        <p className="mt-2 text-sm text-fg/55 leading-relaxed line-clamp-2">{item.reason}</p>
      )}

      {/* Imagem do POI se existir */}
      {d.imageUrl && (
        <img src={d.imageUrl} alt={item.title}
          className="mt-3 w-full h-36 rounded-lg object-cover border border-fg/10" />
      )}

      {/* Dados chave */}
      {dataEntries.length > 0 && (
        <div className="mt-3 rounded-lg border border-fg/8 bg-black/20 p-3 grid gap-1">
          {dataEntries.slice(0, 5).map(([k, v]) => (
            v !== null && v !== undefined && (
              <div key={k} className="flex gap-2 text-xs">
                <span className="min-w-[100px] font-bold text-fg/35">{k}</span>
                <span className="text-fg/65 break-all">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
              </div>
            )
          ))}
        </div>
      )}



      <div className="mt-2 text-xs text-fg/25">
        {new Date(item.createdAt).toLocaleString('pt-PT')}
        {item.generatedBy && ` · ${item.generatedBy}`}
        {item.reviewNote && ` · "${item.reviewNote}"`}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {canAct && (
          <>
            <button onClick={() => onAction(item, 'approve')} disabled={acting === item.id}
              className="rounded border border-forest-light/30 bg-forest-light/10 px-3 py-1.5 text-xs font-bold text-forest-light hover:bg-forest-light/20 disabled:opacity-50">
              Aprovar
            </button>
            {isPending && (
              <button onClick={() => onAction(item, 'backlog')} disabled={acting === item.id}
                className="rounded border border-fg/15 bg-fg/5 px-3 py-1.5 text-xs font-bold text-fg/55 hover:bg-fg/10 disabled:opacity-50">
                Backlog
              </button>
            )}
            <button onClick={() => onAction(item, 'reject')} disabled={acting === item.id}
              className="rounded border border-energy/25 bg-energy/5 px-3 py-1.5 text-xs font-bold text-energy hover:bg-energy/15 disabled:opacity-50">
              Rejeitar
            </button>
          </>
        )}
        {item.status !== 'approved' && (
          <button onClick={() => onDelete(item.id)} disabled={acting === item.id}
            className="ml-auto rounded border border-fg/10 bg-black/20 px-2 py-1.5 text-xs text-fg/25 hover:text-energy hover:border-energy/20 disabled:opacity-50"
            title="Apagar sugestão">
            ✕
          </button>
        )}
      </div>
    </motion.div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function AdminSuggestionsPage() {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [notice, setNotice] = useState('');
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null);
  const [acting, setActing] = useState(null);
  const [researchOpen, setResearchOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [filterCity, setFilterCity] = useState('');

  useEffect(() => {
    adminService.cities.list({ limit: 50 }).then((r) => setCities(r.data.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (status, cityId) => {
    setLoading(true); setErr('');
    try {
      const params = { status, limit: 50 };
      if (cityId) params.cityId = cityId;
      const res = await adminService.suggestions.list(params);
      setItems(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch {
      setErr('Não foi possível carregar as sugestões.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab, filterCity); }, [tab, filterCity, load]);

  const generate = async () => {
    setGenerating(true); setNotice(''); setErr('');
    try {
      const res = await adminService.suggestions.generate();
      setNotice(res.data.data?.message || 'Sugestões geradas.');
      if (tab === 'pending') load('pending', filterCity);
    } catch (e) {
      setErr(e.response?.data?.message || 'Erro ao gerar sugestões.');
    } finally { setGenerating(false); }
  };

  const handleAction = (item, action) => setModal({ item, action });

  const handleDelete = async (id) => {
    setActing(id);
    try {
      await adminService.suggestions.delete(id);
      load(tab, filterCity);
    } catch (e) {
      setErr(e.response?.data?.message || 'Erro ao apagar.');
    } finally { setActing(null); }
  };

  const confirmAction = async (note) => {
    if (!modal) return;
    const { item, action } = modal;
    setActing(item.id); setErr('');
    try {
      if (action === 'approve') await adminService.suggestions.approve(item.id, { note });
      else if (action === 'reject') await adminService.suggestions.reject(item.id, { note });
      else if (action === 'backlog') await adminService.suggestions.backlog(item.id, { note });
      const msgs = { approve: 'Aprovada e criada no jogo.', reject: 'Rejeitada.', backlog: 'Movida para backlog.' };
      setNotice(msgs[action]);
      setModal(null);
      load(tab, filterCity);
    } catch (e) {
      setErr(e.response?.data?.message || 'Erro ao processar ação.');
    } finally { setActing(null); }
  };

  return (
    <section className="min-h-full">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">IA · Aprovação de conteúdo</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Sugestões dos Agentes</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">
            Usa <strong className="text-gem/80">Pesquisar POIs</strong> para descobrir locais reais novos com o pipeline de 4 agentes.
            O botão <strong className="text-accent/80">Gerar agora</strong> sugere missões e alertas de segurança.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setResearchOpen(true)}
            className="rounded-lg border border-gem/40 bg-gem/10 px-4 py-2 text-sm font-bold text-gem hover:bg-gem/20"
          >
            🔍 Pesquisar POIs
          </button>
          <button onClick={generate} disabled={generating}
            className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20 disabled:opacity-60 disabled:cursor-wait">
            {generating ? 'A gerar...' : 'Gerar agora'}
          </button>
        </div>
      </div>

      {(err || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${err ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {err || notice}
        </div>
      )}

      {/* Tabs + filtro de cidade */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-lg border border-fg/10 bg-black/20 p-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`rounded-md px-4 py-1.5 text-sm font-bold transition-colors ${tab === t.key ? 'bg-accent/20 text-accent' : 'text-fg/45 hover:text-fg/70'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <select
          value={filterCity}
          onChange={(e) => setFilterCity(e.target.value)}
          className="rounded-lg border border-fg/15 bg-black/30 px-3 py-1.5 text-sm text-fg/70 focus:border-accent/40 focus:outline-none"
        >
          <option value="">Todas as cidades</option>
          {cities.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Lista */}
      <div className="mt-5">
        {loading ? (
          <div className="rounded-lg border border-fg/10 bg-black/15 p-8 text-center text-sm text-fg/40">A carregar...</div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-fg/10 bg-black/15 p-8 text-center text-sm text-fg/40">
            {tab === 'pending'
              ? 'Sem sugestões pendentes. Clica em "Pesquisar POIs" ou "Gerar agora".'
              : `Sem sugestões em "${TABS.find((t) => t.key === tab)?.label}".`}
          </div>
        ) : (
          <>
            <div className="mb-3 text-xs text-fg/35">{total} resultado(s)</div>
            <AnimatePresence mode="popLayout">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {items.map((item) => (
                  <SuggestionCard key={item.id} item={item} onAction={handleAction} onDelete={handleDelete} acting={acting} />
                ))}
              </div>
            </AnimatePresence>
          </>
        )}
      </div>

      <NoteModal open={Boolean(modal)} onClose={() => setModal(null)} onConfirm={confirmAction}
        action={modal?.action} loading={acting === modal?.item?.id} />

      <POIResearchModal
        open={researchOpen}
        onClose={() => setResearchOpen(false)}
        onSuccess={() => { setResearchOpen(false); setTab('pending'); load('pending'); }}
      />
    </section>
  );
}
